import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { ICONS } from '../components/icons/registry'
import CalendlyButton from '../components/CalendlyButton'
import SEO from '../components/SEO'
import Breadcrumbs from '../components/Breadcrumbs'
import { generateServiceSchema, generateLocalBusinessSchema, generateFAQSchema } from '../lib/structured-data-utils'
import CtaSection from '../components/CtaSection'

const DevOpsServicesDubai = () => {
  const currentUrl = typeof window !== 'undefined' ? window.location.href : ''

  const services = [
    {
      id: 'ci-cd-pipelines',
      title: 'CI/CD Pipeline Design',
      description: 'Build robust continuous integration and continuous deployment pipelines that automate your software delivery process. We implement industry best practices using tools like Jenkins, GitLab CI, GitHub Actions, and Azure DevOps.',
      icon: ICONS.cicdPipeline,
      features: [
        'Automated Build & Test Pipelines',
        'Multi-environment Deployment Strategies',
        'Rollback & Recovery Automation',
        'Pipeline Security Scanning',
        'Artifact Management & Versioning',
        'Deployment Approval Workflows'
      ]
    },
    {
      id: 'infrastructure-as-code',
      title: 'Infrastructure as Code (IaC)',
      description: 'Define and manage your infrastructure through code for consistency, version control, and rapid provisioning. We use Terraform, CloudFormation, Pulumi, and Ansible to automate your infrastructure.',
      icon: ICONS.infrastructureAsCode,
      features: [
        'Terraform Module Development',
        'AWS CloudFormation Templates',
        'Ansible Playbook Automation',
        'Infrastructure Version Control',
        'Environment Parity Guarantee',
        'Drift Detection & Remediation'
      ]
    },
    {
      id: 'monitoring-observability',
      title: 'Monitoring & Observability',
      description: 'Gain complete visibility into your applications and infrastructure with comprehensive monitoring, logging, and tracing solutions. We implement observability stacks that help you detect and resolve issues before they impact users.',
      icon: ICONS.monitoring,
      features: [
        'Prometheus & Grafana Dashboards',
        'ELK Stack Implementation',
        'Distributed Tracing (Jaeger, Zipkin)',
        'Custom Alert Configuration',
        'SLA/SLO Monitoring',
        'Incident Response Automation'
      ]
    },
    {
      id: 'cloud-migration',
      title: 'Cloud Migration & Optimization',
      description: 'Migrate your workloads to the cloud with minimal disruption and maximum efficiency. We help you optimize costs, improve performance, and leverage cloud-native services on AWS, Azure, and GCP.',
      icon: ICONS.migration,
      features: [
        'Cloud Readiness Assessment',
        'Migration Strategy & Planning',
        'Workload Containerization',
        'Cost Optimization Analysis',
        'Multi-cloud Architecture',
        'Hybrid Cloud Solutions'
      ]
    }
  ]

  const tools = [
    { name: 'Jenkins', category: 'CI/CD' },
    { name: 'GitLab CI', category: 'CI/CD' },
    { name: 'GitHub Actions', category: 'CI/CD' },
    { name: 'Terraform', category: 'IaC' },
    { name: 'Ansible', category: 'IaC' },
    { name: 'Docker', category: 'Containers' },
    { name: 'Kubernetes', category: 'Orchestration' },
    { name: 'Prometheus', category: 'Monitoring' },
    { name: 'Grafana', category: 'Observability' },
    { name: 'ArgoCD', category: 'GitOps' },
    { name: 'Helm', category: 'K8s' },
    { name: 'AWS', category: 'Cloud' }
  ]

  const benefits = [
    {
      title: 'Faster Time to Market',
      description: 'Reduce deployment cycles from weeks to hours with automated pipelines and streamlined release processes.',
      stat: '10x'
    },
    {
      title: 'Improved Reliability',
      description: 'Achieve higher uptime with automated testing, monitoring, and self-healing infrastructure.',
      stat: '99.9%'
    },
    {
      title: 'Cost Optimization',
      description: 'Reduce infrastructure costs through automation, right-sizing, and efficient resource utilization.',
      stat: '40%'
    },
    {
      title: 'Team Productivity',
      description: 'Free your developers from manual operations to focus on building features that drive business value.',
      stat: '3x'
    }
  ]

  const processSteps = [
    {
      step: '01',
      title: 'Assessment & Audit',
      description: 'We evaluate your current development workflow, infrastructure, deployment processes, and team capabilities. This includes reviewing your codebase, CI/CD maturity, infrastructure setup, and security posture to identify bottlenecks and quick wins.'
    },
    {
      step: '02',
      title: 'Strategy & Roadmap',
      description: 'Based on the assessment, we create a prioritized DevOps transformation roadmap tailored to your business goals. We define KPIs, select the right tools for your stack, and plan the implementation in phases to minimize disruption.'
    },
    {
      step: '03',
      title: 'Implementation & Automation',
      description: 'Our engineers implement CI/CD pipelines, infrastructure as code, monitoring, and security scanning. We work alongside your team, building automation that fits your workflow rather than forcing a one-size-fits-all approach.'
    },
    {
      step: '04',
      title: 'Knowledge Transfer & Support',
      description: 'We document everything, train your team on the new tools and processes, and provide ongoing support. Our goal is to make your team self-sufficient while remaining available for complex challenges and optimization.'
    }
  ]

  const faqs = [
    {
      question: 'What DevOps services do you offer in Dubai?',
      answer: 'We offer end-to-end DevOps consulting and implementation services in Dubai including CI/CD pipeline design and automation, infrastructure as code (IaC) with Terraform and CloudFormation, container orchestration with Kubernetes and Docker, monitoring and observability setup with Prometheus and Grafana, cloud migration to AWS, Azure, or GCP, and DevSecOps integration. We work with startups, SMEs, and enterprises across the UAE.'
    },
    {
      question: 'How long does a typical DevOps transformation take?',
      answer: 'A typical DevOps transformation for a Dubai business takes 4 to 12 weeks depending on the scope and complexity. A basic CI/CD pipeline setup can be completed in 2 to 3 weeks. A full transformation including infrastructure as code, monitoring, and security automation usually takes 8 to 12 weeks. We use a phased approach so you see results from the first sprint.'
    },
    {
      question: 'How much do DevOps services cost in Dubai?',
      answer: 'DevOps consulting rates in Dubai typically range from AED 500 to AED 1,500 per hour depending on the expertise required. Project-based engagements for CI/CD setup start from AED 25,000, while comprehensive DevOps transformations range from AED 75,000 to AED 250,000. We offer flexible engagement models including fixed-price projects, time and materials, and monthly retainers. Contact us for a custom quote based on your specific requirements.'
    },
    {
      question: 'Which DevOps tools and technologies do you work with?',
      answer: 'We work with all major DevOps tools including Jenkins, GitLab CI, GitHub Actions, and Azure DevOps for CI/CD. For infrastructure as code we use Terraform, AWS CloudFormation, Pulumi, and Ansible. We implement Docker and Kubernetes for containerization, Prometheus, Grafana, and the ELK stack for monitoring, and ArgoCD and Flux for GitOps. We select tools based on your existing stack and team expertise rather than imposing a specific toolchain.'
    },
    {
      question: 'Can you help migrate our on-premise infrastructure to the cloud?',
      answer: 'Yes, cloud migration is one of our core DevOps services in Dubai. We handle the full migration lifecycle including assessment of your current infrastructure, selection of the right cloud provider (AWS, Azure, or GCP), containerization of workloads, data migration planning, and post-migration optimization. We specialize in lift-and-shift, re-platforming, and cloud-native refactoring strategies depending on your applications and business requirements.'
    },
    {
      question: 'Do you provide ongoing DevOps support after implementation?',
      answer: 'Yes, we offer flexible support packages after the initial implementation. This includes 24/7 monitoring and incident response, pipeline maintenance and optimization, infrastructure scaling and cost optimization, security patching and compliance updates, and monthly performance reviews. Many of our Dubai clients start with a project engagement and transition to a monthly retainer for ongoing DevOps support.'
    },
    {
      question: 'What industries do you serve with DevOps services in Dubai?',
      answer: 'We serve a wide range of industries in Dubai and the UAE including fintech and banking, e-commerce and retail, healthcare and medtech, logistics and supply chain, real estate and proptech, government and public sector, and SaaS companies. Our DevOps practices are adapted to meet industry-specific compliance requirements such as PCI DSS for fintech and HIPAA for healthcare.'
    },
    {
      question: 'What is the difference between DevOps and DevSecOps?',
      answer: 'DevOps focuses on automating and streamlining the software delivery pipeline through CI/CD, infrastructure as code, and monitoring. DevSecOps extends this by integrating security practices at every stage of the pipeline rather than treating security as a separate phase. This includes automated security scanning in CI/CD pipelines, infrastructure security auditing, container vulnerability scanning, and compliance as code. We offer both DevOps and DevSecOps services in Dubai.'
    }
  ]

  const whyUs = [
    {
      title: 'Dubai-Based Team',
      description: 'Our DevOps engineers are based in Dubai and understand the local business landscape, regulations, and data residency requirements across UAE free zones.'
    },
    {
      title: 'Certified Engineers',
      description: 'Our team holds AWS, Kubernetes (CKA/CKAD), Terraform, and Azure certifications. We bring hands-on experience from deploying production systems across the Middle East.'
    },
    {
      title: 'Tool-Agnostic Approach',
      description: 'We recommend tools based on your existing stack and team capabilities, not vendor partnerships. Whether you use AWS, Azure, GCP, or hybrid infrastructure, we adapt.'
    },
    {
      title: 'Transparent Pricing',
      description: 'No hidden fees or scope creep. We provide detailed proposals with fixed milestones and deliverables so you know exactly what you are paying for before the project starts.'
    }
  ]

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.15 }
    }
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 }
    }
  }

  const serviceSchemas = services.map(service => generateServiceSchema({
    name: `${service.title} in Dubai`,
    description: service.description,
    provider: 'Demirix',
    areaServed: 'Dubai, UAE'
  }))

  const localBusinessSchema = generateLocalBusinessSchema()
  const faqSchema = generateFAQSchema(faqs)
  const structuredData = [...serviceSchemas, localBusinessSchema, faqSchema]

  const breadcrumbs = [
    { name: 'Services', path: '/services' },
    { name: 'DevOps Services Dubai', path: '/devops-services-dubai' }
  ]

  return (
    <div className="pt-20 bg-white dark:bg-gray-900">
      <SEO
        title="DevOps Services Dubai | CI/CD, Infrastructure Automation & Cloud Migration UAE"
        description="Expert DevOps services in Dubai. We implement CI/CD pipelines, infrastructure as code, monitoring solutions, and cloud migration strategies. Transform your software delivery with Demirix."
        keywords={[
          'devops services dubai',
          'devops consulting uae',
          'ci/cd pipeline dubai',
          'infrastructure as code dubai',
          'cloud migration dubai',
          'devops automation uae',
          'jenkins consulting dubai',
          'terraform consulting uae',
          'kubernetes devops dubai',
          'gitops dubai',
          'devops engineer dubai',
          'site reliability engineering uae'
        ]}
        url={currentUrl}
        structuredData={structuredData}
      />
      <Breadcrumbs customCrumbs={breadcrumbs} />

      {/* Hero Section */}
      <section className="min-h-[80vh] flex items-center justify-center bg-gradient-to-b from-gray-50 to-white dark:from-gray-800 dark:to-gray-900 relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-96 h-96 bg-primary-100 dark:bg-primary-900/20 rounded-full blur-3xl opacity-50" />
          <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-primary-100 dark:bg-primary-900/20 rounded-full blur-3xl opacity-50" />
        </div>

        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-4xl mx-auto"
          >
            <span className="inline-block px-4 py-2 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded-full text-sm font-medium mb-6">
              DevOps & Automation
            </span>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-gray-900 dark:text-white mb-6 leading-tight">
              DevOps Services in Dubai
            </h1>
            <p className="text-lg sm:text-xl text-gray-600 dark:text-gray-300 mb-8 max-w-3xl mx-auto">
              Accelerate your software delivery with modern DevOps practices. We help Dubai businesses
              implement CI/CD pipelines, infrastructure automation, and observability solutions that
              reduce time to market and improve reliability.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <CalendlyButton className="inline-flex items-center justify-center px-8 py-4 bg-primary-600 hover:bg-primary-700 text-white font-semibold rounded-lg transition-all duration-200 transform hover:scale-105 w-full sm:w-auto">
                Schedule DevOps Consultation
              </CalendlyButton>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center px-8 py-4 border-2 border-primary-600 dark:border-primary-400 text-primary-600 dark:text-primary-400 hover:bg-primary-600 hover:text-white dark:hover:bg-primary-400 dark:hover:text-gray-900 font-semibold rounded-lg transition-all duration-200 w-full sm:w-auto"
              >
                Get a Quote
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Benefits/Stats Section */}
      <section className="section-padding bg-primary-600 dark:bg-primary-700">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={containerVariants}
            className="grid grid-cols-2 lg:grid-cols-4 gap-8"
          >
            {benefits.map((benefit, index) => (
              <motion.div key={index} variants={itemVariants} className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-white mb-2">
                  {benefit.stat}
                </div>
                <div className="text-lg font-semibold text-white mb-1">
                  {benefit.title}
                </div>
                <div className="text-primary-100 text-sm">
                  {benefit.description}
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section className="section-padding">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={containerVariants}
          >
            <motion.div variants={itemVariants} className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
                Our DevOps Services
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
                End-to-end DevOps solutions designed for Dubai businesses looking to modernize
                their software delivery and infrastructure management.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-8">
              {services.map((service) => {
                const Icon = service.icon
                return (
                  <motion.div
                    key={service.id}
                    variants={itemVariants}
                    className="bg-gray-50 dark:bg-gray-800 rounded-2xl p-8 hover:shadow-xl transition-all duration-300 border border-gray-100 dark:border-gray-700"
                  >
                    <div className="flex items-start gap-4 mb-6">
                      <div className="w-14 h-14 flex-shrink-0" aria-hidden="true">
                        <Icon className="w-full h-full" darkTreatment="none" />
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                          {service.title}
                        </h3>
                        <p className="text-gray-600 dark:text-gray-300">
                          {service.description}
                        </p>
                      </div>
                    </div>
                    <ul className="space-y-3">
                      {service.features.map((feature, index) => (
                        <li key={index} className="flex items-start">
                          <svg className="w-5 h-5 text-primary-600 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          <span className="text-gray-700 dark:text-gray-200">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </motion.div>
                )
              })}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Tools & Technologies */}
      <section className="section-padding bg-gray-50 dark:bg-gray-800">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={containerVariants}
          >
            <motion.div variants={itemVariants} className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
                Tools & Technologies We Use
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
                We leverage industry-leading DevOps tools to build reliable, scalable automation solutions.
              </p>
            </motion.div>

            <motion.div variants={itemVariants} className="flex flex-wrap justify-center gap-4">
              {tools.map((tool, index) => (
                <div
                  key={index}
                  className="px-6 py-3 bg-white dark:bg-gray-700 rounded-full border border-gray-200 dark:border-gray-600 shadow-sm hover:shadow-md transition-shadow"
                >
                  <span className="font-medium text-gray-900 dark:text-white">{tool.name}</span>
                  <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">{tool.category}</span>
                </div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* How We Work / Process */}
      <section className="section-padding">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={containerVariants}
          >
            <motion.div variants={itemVariants} className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
                How We Implement DevOps
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
                A proven four-phase approach that delivers measurable results from the first sprint,
                tailored to your team's maturity and business objectives.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {processSteps.map((step) => (
                <motion.div
                  key={step.step}
                  variants={itemVariants}
                  className="relative"
                >
                  <div className="text-6xl font-bold text-primary-100 dark:text-primary-900/30 mb-4">
                    {step.step}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                    {step.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 text-sm leading-relaxed">
                    {step.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why Demirix */}
      <section className="section-padding bg-gray-50 dark:bg-gray-800">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={containerVariants}
          >
            <motion.div variants={itemVariants} className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
                Why Choose Demirix for DevOps in Dubai
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
                We combine deep technical expertise with local market knowledge to deliver
                DevOps solutions that work for businesses operating in the UAE.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-8">
              {whyUs.map((item, index) => (
                <motion.div
                  key={index}
                  variants={itemVariants}
                  className="flex gap-4"
                >
                  <div className="flex-shrink-0 w-12 h-12 bg-primary-100 dark:bg-primary-900/30 rounded-lg flex items-center justify-center">
                    <svg className="w-6 h-6 text-primary-600 dark:text-primary-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                      {item.title}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-300">
                      {item.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="section-padding">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={containerVariants}
          >
            <motion.div variants={itemVariants} className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
                Frequently Asked Questions
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
                Common questions about DevOps services in Dubai answered by our engineering team.
              </p>
            </motion.div>

            <div className="max-w-3xl mx-auto space-y-6">
              {faqs.map((faq, index) => (
                <motion.div
                  key={index}
                  variants={itemVariants}
                  className="bg-gray-50 dark:bg-gray-800 rounded-xl p-6 border border-gray-100 dark:border-gray-700"
                >
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-3">
                    {faq.question}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                    {faq.answer}
                  </p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Cross-link to other services */}
      <section className="section-padding">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-4">
              Related Services
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-300 mb-6 max-w-2xl mx-auto">
              Explore our other specialized technology services for Dubai businesses.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link
                to="/kubernetes-consulting-dubai"
                className="inline-flex items-center px-6 py-3 border-2 border-primary-600 dark:border-primary-400 text-primary-600 dark:text-primary-400 hover:bg-primary-600 hover:text-white dark:hover:bg-primary-400 dark:hover:text-gray-900 font-semibold rounded-lg transition-all duration-200"
              >
                Kubernetes Consulting
              </Link>
              <Link
                to="/devsecops-dubai"
                className="inline-flex items-center px-6 py-3 border-2 border-primary-600 dark:border-primary-400 text-primary-600 dark:text-primary-400 hover:bg-primary-600 hover:text-white dark:hover:bg-primary-400 dark:hover:text-gray-900 font-semibold rounded-lg transition-all duration-200"
              >
                DevSecOps Services
              </Link>
              <Link
                to="/aws-cloud-consulting-dubai"
                className="inline-flex items-center px-6 py-3 border-2 border-primary-600 dark:border-primary-400 text-primary-600 dark:text-primary-400 hover:bg-primary-600 hover:text-white dark:hover:bg-primary-400 dark:hover:text-gray-900 font-semibold rounded-lg transition-all duration-200"
              >
                AWS Cloud Consulting
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <CtaSection
        heading="Ready to Accelerate Your Software Delivery?"
        description="Let's discuss how DevOps can transform your development workflow and help your Dubai business deliver software faster and more reliably."
        primary={{ kind: 'calendly', label: 'Schedule Free Consultation' }}
        secondary={{ kind: 'link', label: 'Contact Us', href: '/contact' }}
      />
    </div>
  )
}

export default DevOpsServicesDubai
